import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    FaRegClock, FaCalendarCheck, FaHourglassHalf, FaUmbrellaBeach,
    FaPlaneDeparture, FaListAlt, FaRegEdit, FaChevronRight,
} from "react-icons/fa";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./Dashboard.css";

const getMonday = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d;
};
const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtHrs = (n) => {
    const v = Number(n) || 0;
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
};
const statusPill = (s) => {
    const key = (s || "Pending").toLowerCase();
    if (key === "approved") return "dz-pill dz-pill-success";
    if (key === "rejected") return "dz-pill dz-pill-danger";
    if (key === "canceled") return "dz-pill dz-pill-id";
    return "dz-pill dz-pill-warn";
};

const Dashboard = ({ navSize }) => {
    const navigate = useNavigate();
    const [emp, setEmp] = useState(null);
    const [weekHours, setWeekHours] = useState(0);
    const [leaveBalance, setLeaveBalance] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [nextHoliday, setNextHoliday] = useState(null);
    const [punchActive, setPunchActive] = useState(false);

    // Punch-based "This Week's Hours" (completed sessions, Mon–Sun) + live status
    const refreshPunch = useCallback(async () => {
        try {
            const monday = getMonday(new Date());
            const weekEnd = new Date(monday);
            weekEnd.setDate(weekEnd.getDate() + 7);

            const [histRes, statusRes] = await Promise.all([
                apiFetch(`/api/punch/history`, { credentials: "include" }),
                apiFetch(`/api/punch/status`, { credentials: "include" }),
            ]);

            const hist = await histRes.json();
            let total = 0;
            (Array.isArray(hist) ? hist : []).forEach((e) => {
                if (e.punch_in && e.punch_out) {
                    const inD = new Date(e.punch_in);
                    if (inD >= monday && inD < weekEnd) total += (new Date(e.punch_out) - inD) / 3600000;
                }
            });
            setWeekHours(Math.round(total * 100) / 100);

            const status = await statusRes.json();
            setPunchActive(!!status.active);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => {
        const now = new Date();

        const fetchEmp = async () => {
            try {
                const res = await apiFetch(`/api/employee/logined_employee`, { credentials: "include" });
                const data = await res.json();
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login"); return;
                }
                if (data[0]?.emp_id) setEmp(data[0]);
            } catch (e) { console.error(e); }
        };
        const fetchBalance = async () => {
            try {
                const res = await apiFetch(`/api/Leave/my_leave_balance`, { credentials: "include" });
                const data = await res.json();
                setLeaveBalance(Array.isArray(data) ? data : []);
            } catch (e) { console.error(e); }
        };
        const fetchLeaves = async () => {
            try {
                const res = await apiFetch(`/api/leave/my_total_leaves`, { credentials: "include" });
                const data = await res.json();
                setLeaves(Array.isArray(data) ? data : []);
            } catch (e) { console.error(e); }
        };
        const fetchHoliday = async () => {
            try {
                const res = await apiFetch(`/api/holiday/list?month=${now.getMonth() + 1}&year=${now.getFullYear()}`, { credentials: "include" });
                const data = await res.json();
                const upcoming = (Array.isArray(data) ? data : [])
                    .filter(h => new Date(h.holiday_date) >= new Date(now.toDateString()))
                    .sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date));
                setNextHoliday(upcoming[0] || null);
            } catch (e) { console.error(e); }
        };

        fetchEmp(); fetchBalance(); fetchLeaves(); fetchHoliday(); refreshPunch();
    }, [navigate, refreshPunch]);

    // Live-update when the header punches in/out
    useEffect(() => {
        const handler = () => refreshPunch();
        window.addEventListener("dtime:punch-changed", handler);
        return () => window.removeEventListener("dtime:punch-changed", handler);
    }, [refreshPunch]);

    const approvedCount = leaves.filter(l => l.leave_status === "Approved").length;
    const pendingCount = leaves.filter(l => l.leave_status === "Pending").length;
    const firstName = emp?.emp_first_name ? emp.emp_first_name.split(" ")[0] : "there";
    const recentLeaves = [...leaves].slice(0, 5);

    const tiles = [
        { icon: <FaRegClock />, label: "This Week's Hours", value: fmtHrs(weekHours), cls: "dz-blue" },
        { icon: <FaUmbrellaBeach />, label: "Approved Leaves", value: approvedCount, cls: "dz-green" },
        { icon: <FaHourglassHalf />, label: "Pending Leaves", value: pendingCount, cls: "dz-amber" },
        {
            icon: <FaCalendarCheck />, label: "Next Holiday",
            value: nextHoliday ? nextHoliday.holiday_name : "—",
            sub: nextHoliday ? fmtDate(nextHoliday.holiday_date) : "None this month", cls: "dz-indigo",
        },
    ];

    const quickLinks = [
        { to: "/My_leave_requests", label: "Apply / View Leave", icon: <FaPlaneDeparture /> },
        { to: "/Timesheet_entry", label: "Fill Timesheet", icon: <FaRegEdit /> },
        { to: "/timesheet-summary", label: "Timesheet Summary", icon: <FaListAlt /> },
        { to: "/Holiday", label: "Holiday Calendar", icon: <FaCalendarCheck /> },
    ];

    return (
        <div className="db-container">
            <div className={`db-page ${navSize}`}>
                <div className="header-wrapper">
                    <h2 className="dz-page-title">
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dTime Logo" className="page-logo" />
                        </a>
                        Dashboard
                    </h2>
                </div>

                <p className="db-greeting">Welcome back, <strong>{firstName}</strong> 👋 — here's your snapshot.</p>

                {/* Stat tiles */}
                <div className="dz-tile-grid db-tiles">
                    {tiles.map((t, i) => (
                        <div key={i} className={`dz-tile ${t.cls}`}>
                            <div className="db-tile-top">
                                <span className="db-tile-icon">{t.icon}</span>
                                <span className="dz-tile-label">{t.label}</span>
                            </div>
                            <div className="dz-tile-value db-tile-value">{t.value}</div>
                            {t.sub && <div className="db-tile-sub">{t.sub}</div>}
                        </div>
                    ))}
                </div>

                <div className="db-columns">
                    {/* Recent leave requests */}
                    <div className="dz-card db-card">
                        <div className="db-card-head">
                            <h3>Recent Leave Requests</h3>
                            <NavLink to="/My_leave_requests" className="db-link">View all <FaChevronRight /></NavLink>
                        </div>
                        <div className="dz-table-wrap">
                            <table className="dz-table">
                                <thead>
                                    <tr><th>Type</th><th>Start</th><th>End</th><th>Status</th></tr>
                                </thead>
                                <tbody>
                                    {recentLeaves.length === 0 ? (
                                        <tr><td colSpan={4} className="dz-table-empty">No leave requests yet.</td></tr>
                                    ) : recentLeaves.map(l => (
                                        <tr key={l.leave_requests_id}>
                                            <td>{l.leave_type}</td>
                                            <td>{fmtDate(l.start_date)}</td>
                                            <td>{fmtDate(l.end_date)}</td>
                                            <td><span className={statusPill(l.leave_status)}>{l.leave_status || "Pending"}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="db-side">
                        {/* Punch status */}
                        <div className="dz-card db-status-card">
                            <span className="dz-tile-label">Punch Status</span>
                            <div className={`db-status ${punchActive ? "in" : "out"}`}>
                                <span className="db-status-dot" />
                                {punchActive ? "Punched In" : "Not Punched In"}
                            </div>
                            <div className="db-status-hint">
                                {punchActive
                                    ? "You're currently clocked in. Remember to punch out."
                                    : "Use the Punch In button in the header to start your day."}
                            </div>
                        </div>

                        {/* Quick actions */}
                        <div className="dz-card">
                            <span className="dz-tile-label">Quick Actions</span>
                            <div className="db-links">
                                {quickLinks.map((q) => (
                                    <NavLink key={q.to} to={q.to} className="db-quick">
                                        <span className="db-quick-icon">{q.icon}</span>
                                        {q.label}
                                        <FaChevronRight className="db-quick-arrow" />
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Leave balance breakdown */}
                <div className="dz-card db-card db-balance">
                    <div className="db-card-head"><h3>Leave Balance</h3></div>
                    <div className="dz-table-wrap">
                        <table className="dz-table">
                            <thead>
                                <tr><th>Leave Type</th><th>Max</th><th>Balance</th></tr>
                            </thead>
                            <tbody>
                                {leaveBalance.length === 0 ? (
                                    <tr><td colSpan={3} className="dz-table-empty">No leave types configured.</td></tr>
                                ) : leaveBalance.map((l) => (
                                    <tr key={l.leave_type_id}>
                                        <td>{l.leave_type}</td>
                                        <td>{fmtHrs(l.max_leaves)}</td>
                                        <td><strong>{fmtHrs(l.balance_leave)}</strong></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
